<script setup>
import {computed, inject, reactive, ref} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog} from "primevue";
import {Icon} from "@iconify/vue";
import Users from "@/components/Users.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useWalletStore} from "@/stores/wallet.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {useVuelidate} from "@vuelidate/core";
import {useI18n} from "vue-i18n";

//stores
const userStore = useUserStore();
const walletStore = useWalletStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const confirm = useConfirm();
const {t, locale} = useI18n({useScope: 'global'})
//emits
const emit = defineEmits(['refresh', 'showUserPermissions']);

//props

//injects
const visible = inject('isShowCreateWalletModal')

//provides

//component variables
const wallet = reactive({
  "asset": '',
  "userID": null
})
const rules = computed(() => {
  return {
    asset: {required},
  };
});
const $vuelidate = useVuelidate(rules, wallet);

//global functions

//component functions
async function createWallet() {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  try {
    const res = await walletStore.action_createWallet({asset: wallet.asset, userID: Number(wallet.userID)})
    if (res.result === true) {
      //toast.add({severity: 'success', summary: 'Successful', detail: `${wallet.asset} wallet created`, life: 500});
      showMessage(`${wallet.asset} wallet created`, 'success')
      visible.value = false
      await walletStore.action_getWallets();
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setSaving(false)
  }
}

function getSelectedUserID(user) {
  wallet.userID = user.id
}

//hooks

</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ $t('message.create wallet') }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <FloatLabel variant="on">
          <InputText id="asset" v-model="wallet.asset" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.asset.$error }"/>
          <label for="asset" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.asset.$error }">{{ $t('message.asset.label') }}</label>
        </FloatLabel>
        <template v-if="$vuelidate.asset.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.asset.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <Users v-if="userStore.hasPermission('BIG_BOSS')"
               @update:modelValue="getSelectedUserID" appendTo="body"
               :label="$t('message.username.label')" :required="false"/>
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button @click="createWallet"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="$vuelidate.$invalid">
        {{ $t('message.add') }}
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style></style>